import { Button } from '@sunwebgroup/uds-ui/button'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { PaymentAmounts } from '@/scopes/payment/components/PaymentAmounts'
import { PaymentFaq } from '@/scopes/payment/components/PaymentFaq/PaymentFaq'
import { PaymentFullyPaidBanner } from '@/scopes/payment/components/PaymentFullyPaidBanner'
import { PaymentMethods } from '@/scopes/payment/components/PaymentMethods'
import { PaymentResultBanner } from '@/scopes/payment/components/PaymentResultBanner'
import { usePaymentActions, usePaymentStep } from '@/scopes/payment/domain/PaymentContext'
import { isFullOnlyPayment } from '@/scopes/payment/domain/policies/fullOnlyPayment'
import { isBookingFullyPaid } from '@/scopes/payment/domain/policies/fullyPaidBooking'
import { hidesPaymentOptions } from '@/scopes/payment/domain/policies/paymentResult'
import { PaymentLoadStatusValues } from '@/scopes/payment/domain/types'
import { useEffectivePaymentResult } from '@/scopes/payment/infrastructure/useEffectivePaymentResult'
import { usePaymentStore } from '@/scopes/payment/infrastructure/usePaymentStore'
import styles from './Payment.module.css'

export function Payment() {
  const intl = useIntl()
  const { isWizard, step } = usePaymentStep()
  const actions = usePaymentActions()

  const selectedAmountType = usePaymentStore(state => state.selectedAmountType)
  const selectedPaymentMethodType = usePaymentStore(state => state.selectedPaymentMethodType)
  const customDepositAmount = usePaymentStore(state => state.customDepositAmount)
  const paymentOptions = usePaymentStore(state => state.paymentOptions)
  const loadStatus = usePaymentStore(state => state.loadStatus)
  const { effectivePaymentResult, voucherApplyResult } = useEffectivePaymentResult()
  const resultBannerMessage = voucherApplyResult
    ? intl.formatMessage({ id: 'payment_scope_voucher_success' }, { amount: intl.formatCurrency(voucherApplyResult.voucher.transactionAmount) })
    : undefined
  const resultBannerSupportingMessage = voucherApplyResult
    ? intl.formatMessage(
        { id: 'payment_scope_voucher_success_details' },
        {
          transactionAmount: intl.formatCurrency(voucherApplyResult.voucher.transactionAmount),
          remainingAmount: intl.formatCurrency(voucherApplyResult.voucher.remainingAmount),
        },
      )
    : undefined
  React.useEffect(() => {
    void actions.pageLoaded()
  }, [actions])

  if (loadStatus === PaymentLoadStatusValues.IDLE || loadStatus === PaymentLoadStatusValues.LOADING) {
    return <Loader />
  }

  if (loadStatus === PaymentLoadStatusValues.ERROR) {
    return (
      <Typography role="alert">{intl.formatMessage({ id: 'load_error' })}</Typography>
    )
  }

  if (!paymentOptions) {
    return (
      <Typography role="alert">{intl.formatMessage({ id: 'load_error' })}</Typography>
    )
  }

  if (hidesPaymentOptions(effectivePaymentResult)) {
    return (
      <PaymentResultBanner
        paymentResult={effectivePaymentResult}
        message={resultBannerMessage}
        supportingMessage={resultBannerSupportingMessage}
      />
    )
  }

  if (isBookingFullyPaid(paymentOptions)) {
    return <PaymentFullyPaidBanner />
  }

  const showAmountChoiceWizard = isWizard && !isFullOnlyPayment(paymentOptions.paymentAmounts)
  const showAmounts = !showAmountChoiceWizard || step === 'amounts'
  const showMethods = !showAmountChoiceWizard || step === 'methods'

  return (
    <>
      <PaymentResultBanner paymentResult={effectivePaymentResult} />
      <PaymentFaq paymentResult={effectivePaymentResult} />
      <div hidden={!showAmounts}>
        <PaymentAmounts
          paymentAmounts={paymentOptions.paymentAmounts}
          selectedAmountType={selectedAmountType}
          onSelectedAmountTypeChange={actions.selectAmountType}
          customDepositAmount={customDepositAmount}
          onCustomDepositAmountChange={actions.setCustomDepositAmount}
          depositDueDate={paymentOptions.nextPayments?.downPaymentDueDate}
          fullPaymentDueDate={paymentOptions.nextPayments?.fullPaymentDueDate}
        />
        {showAmountChoiceWizard && selectedAmountType && (
          <div className={styles.continueButtonWrapper}>
            <Button
              fullWidth
              onClick={() => actions.methodsStepRequested(selectedAmountType)}
              variant="primary"
              type="button"
            >
              {intl.formatMessage({ id: 'payment_scope_amounts_continue_button' })}
            </Button>
          </div>
        )}
      </div>
      <div hidden={!showMethods}>
        <PaymentMethods
          paymentOptions={paymentOptions}
          selectedAmountType={selectedAmountType}
          selectedPaymentMethodType={selectedPaymentMethodType}
          customDepositAmount={customDepositAmount}
          onSelectedPaymentMethodChange={actions.paymentMethodSelected}
          onSelectedPaymentMethodUnavailable={actions.paymentMethodBecameUnavailable}
          onPaymentMethodsUnavailable={actions.paymentMethodsBecameUnavailable}
        />
      </div>
    </>
  )
}

function Loader() {
  return (
    <div className={styles.loader}>
      <IndeterminateProgressCircle size={32} />
    </div>
  )
}
